import React, { useEffect, useState, useCallback } from "react";
import LoadingInComponent from "../../Components/LoadingInComponent";
import WidgetCard from "../../Components/WidgetCard";
import SeatManagementTable from "../../Components/Tables/SeatManagementTable";
import { useToast } from "../../Components/Toast";
import axios from "axios";
import {
  GET_AVAILABILITY_COUNT_API,
  GET_CHECK_IN_WIDGET_API,
  GET_TABLE_DETAIL_LIST_API,
} from "../../Constant/api";
import Button from "../../Components/Button";
import { useNavigate } from "react-router-dom";
import { EVENT_CHECK_IN_PAGE, GUEST_COUNTER } from "../../Router/path";
import Breadcrumb from "../../Components/Breadcrumb";
import Can from "../../Components/Can";

const CheckInList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchCleared, setIsSearchCleared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableDetailData, setTableDetailData] = useState(null);
  const [tableDetailMeta, setTableDetailMeta] = useState(null);
  const [widgetData, setWidgetData] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [tableAvailabilitySelector, setTableAvailabilitySelector] = useState([
    {
      name: "All",
      count: 0,
      color: "bg-[#5C4BF5] border border-[#5C4BF5] text-white",
    },
    {
      name: "Available",
      count: 0,
      color: "bg-[#E9F9EF] border border-[#D3F3DF] text-[#1B9E4B]",
    },
    {
      name: "Full",
      count: 0,
      color: "bg-[#FEF6E6] border border-[#FCECCE] text-[#F0A009]",
    },
    {
      name: "Overflow",
      count: 0,
      color: "bg-[#FFEAEA] border border-[#FFD6D6] text-[#FF3030]",
    },
  ]);
  const [tableAvailability, setTableAvailability] = useState("All");

  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const fetchCheckInList = useCallback(
    async (page = 1, perPageValue = perPage) => {
      setLoading(true);
      try {
        axios
          .get(GET_TABLE_DETAIL_LIST_API, {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
            },
            params: {
              eventId: sessionStorage.getItem("event_id"),
              page: page,
              perPage: perPageValue,
              availability: tableAvailability,
              search: searchTerm,
            },
          })
          .then((response) => {
            setLoading(false);
            setTableDetailData(response.data.data);
            setTableDetailMeta(response.data.meta.pagination);
          })
          .catch((error) => {
            setLoading(false);
            console.error(error);
            toast("Failed to fetch check-in list", "error");
          });
      } catch (error) {
        setLoading(false);
        console.error(error);
        toast("Failed to fetch check-in list", "error");
      }
    },
    [perPage, tableAvailability, searchTerm, toast]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchCheckInList();
    }
  };

  const handlePerPageChange = (e) => {
    setPerPage(e.target.value);
    fetchCheckInList(1, e.target.value);
  };

  const clearFilter = () => {
    setSearchTerm("");
    setIsSearchCleared(true);
  };

  const handlePageClick = (e) => {
    fetchCheckInList(e.selected + 1);
  };

  const fetchWidgetData = useCallback(async () => {
    setLoading(true);
    try {
      axios
        .get(GET_CHECK_IN_WIDGET_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: { eventId: sessionStorage.getItem("event_id") },
        })
        .then((response) => {
          setLoading(false);
          setWidgetData(response.data.data);
        })
        .catch((error) => {
          setLoading(false);
          console.error(error);
          toast("Failed to fetch widget data", "error");
        });
    } catch (error) {
      setLoading(false);
      console.error(error);
      toast("Failed to fetch widget data", "error");
    }
  }, [toast]);

  const fetchAvailabilityCount = useCallback(async () => {
    setLoading(true);
    axios
      .get(GET_AVAILABILITY_COUNT_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          eventId: sessionStorage.getItem("event_id"),
        },
      })
      .then((response) => {
        setLoading(false);
        setTableAvailabilitySelector((prev) =>
          prev.map((item) => ({
            ...item,
            count: response.data.data[item.name.toLowerCase()] || 0,
          }))
        );
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast("Failed to fetch availability count.", "error");
      });
  }, [toast]);

  useEffect(() => {
    fetchWidgetData();
  }, [fetchWidgetData]);

  useEffect(() => {
    fetchCheckInList();
    fetchAvailabilityCount();
  }, [fetchCheckInList, fetchAvailabilityCount]);

  useEffect(() => {
    if (isSearchCleared && searchTerm === "") {
      fetchCheckInList();
      setIsSearchCleared(false);
    }
  }, [isSearchCleared, searchTerm, fetchCheckInList]);

  const breadcrumbItems = [{ label: "Check in" }, { label: "List" }];

  return (
    <div className="flex flex-col w-full h-full">
      <LoadingInComponent isLoading={loading}>
        <div className="flex flex-col w-full h-full p-[16px] md:p-[32px] no-scrollbar overflow-y-auto gap-[16px] md:gap-[24px]">
          {/* Breadcrumb */}
          <Breadcrumb items={breadcrumbItems} />

          {/* Title row */}
          <div className="flex flex-col md:flex-row w-full justify-between items-center">
            <h1 className="text-[29px]">Check In</h1>
            <div className="flex items-center gap-2">
              <Can permission="view_live_dashboard">
                <Button
                  onClick={() => window.open(GUEST_COUNTER, "_blank")}
                  variant="secondary"
                >
                  Live Dashboard
                </Button>
              </Can>
              <Can permission="view_event_check_in_page">
                <Button onClick={() => navigate(EVENT_CHECK_IN_PAGE)}>
                  Open Event Page
                </Button>
              </Can>
            </div>
          </div>

          {/* Widgets */}
          {widgetData && (
            <div className="grid grid-cols-1 md:grid-cols-3 w-full gap-[12px] md:gap-[24px]">
              <WidgetCard name="Total Guests" data={widgetData.total_guests} />
              <WidgetCard
                name="Total Checked In Guests"
                data={widgetData.total_checked_in_guests}
              />
              <WidgetCard
                name="Total Not Checked In Guests"
                data={widgetData.total_not_checked_in_guests}
              />
            </div>
          )}

          {/* Table */}
          {sessionStorage.getItem("seat") &&
            sessionStorage.getItem("seat") === "true" &&
            tableDetailData &&
            tableDetailMeta && (
              <div>
                <Can permission="view_seat_management">
                  <SeatManagementTable
                    data={tableDetailData}
                    meta={tableDetailMeta}
                    tableAvailabilitySelector={tableAvailabilitySelector}
                    tableAvailability={tableAvailability}
                    setTableAvailability={setTableAvailability}
                    searchTerm={searchTerm}
                    handleSearch={handleSearch}
                    handleKeyDown={handleKeyDown}
                    handlePageClick={handlePageClick}
                    handlePerPageChange={handlePerPageChange}
                    perPage={perPage}
                    clearFilter={clearFilter}
                  />
                </Can>
              </div>
            )}
        </div>
      </LoadingInComponent>
    </div>
  );
};

export default CheckInList;
